import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ProgramNode } from '../../../common/models/care-monitor/program-node';
import { ProgramRadioComponent } from '../program-radio/program-radio.component';
import { TranslateModule } from '@ngx-translate/core';
import { ProgramCheckboxComponent } from '../program-checkbox/program-checkbox.component';
import { ProgramOpenTextComponent } from '../program-open-text/program-open-text.component';
import { ProgramOutputComponent } from '../program-output/program-output.component';
import { ProgramEqvasComponent } from '../program-eqvas/program-eqvas.component';
import { ProgramMatrixRadioComponent } from '../program-matrix-radio/program-matrix-radio.component';
import { ProgramNumberComponent } from '../program-number/program-number.component';
import { ProgramMediaComponent } from '../program-media/program-media.component';
import { ProgramDateComponent } from '../program-date/program-date.component';
import { ProgramTextComponent } from '../program-text/program-text.component';
import { ProgramLog } from '../../../common/models/care-monitor/program-log';
import { ProgramPolicyComponent } from '../program-policy/program-policy.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProgramArithmeticComponent } from '../program-arithmetic/program-arithmetic.component';
import { ProgramService } from '@meplis/services';
import { Program } from '@meplis/common';
import { ProgramListInputComponent } from '../program-list-input/program-list-input.component';

type GroupedStep =
  | { kind: 'single'; node: ProgramNode; originalIndex: number }
  | {
      kind: 'group';
      nodes: ProgramNode[];
      startIndex: number;
      endIndex: number;
    };

@Component({
  selector: 'mec-program-step-container',
  imports: [
    CommonModule,
    ProgramRadioComponent,
    ProgramCheckboxComponent,
    ProgramOpenTextComponent,
    ProgramOutputComponent,
    ProgramEqvasComponent,
    ProgramMatrixRadioComponent,
    ProgramNumberComponent,
    ProgramMediaComponent,
    ProgramDateComponent,
    ProgramTextComponent,
    ProgramPolicyComponent,
    ProgramArithmeticComponent,
    ProgramListInputComponent,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './program-step-container.component.html',
  styleUrls: ['./program-step-container.component.scss'],
})
export class ProgramStepContainerComponent implements OnChanges {
  @Input() public programStep: ProgramNode[];
  @Input() public programToken: string;
  @Input() public logs: ProgramLog[];
  @Input() public isLoadingStep: boolean;
  @Output() onStepValidChange = new EventEmitter<boolean>();

  public dependecies: string[];

  // agrupamento dos steps (single | group)
  public groupedSteps: GroupedStep[] = [];

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _programService: ProgramService,
    private _changeDetectorRef: ChangeDetectorRef
  ) {}

  public ngOnChanges(): void {
    if (this.programStep) {
      this.groupSteps(); // <-- agrupa aqui
      this.getDependencies();
      this.getStepLogsUntilCurrent(0);
    }
  }

  private groupSteps(): void {
    const result: GroupedStep[] = [];
    let i = 0;
    const arr = this.programStep || [];

    while (i < arr.length) {
      const step = arr[i];
      if (step.group === 'MATRIX_RADIO' && step.type === 'radio') {
        const start = i;
        const nodes: ProgramNode[] = [];
        while (
          i < arr.length &&
          arr[i].group === 'MATRIX_RADIO' &&
          arr[i].type === 'radio'
        ) {
          nodes.push(arr[i]);
          i++;
        }
        const end = i - 1;
        result.push({ kind: 'group', nodes, startIndex: start, endIndex: end });
      } else {
        result.push({ kind: 'single', node: step, originalIndex: i });
        i++;
      }
    }

    this.groupedSteps = result;
  }

  private getDependencies(): void {
    this.dependecies = this.programStep.flatMap((step) => step.dependencies);
  }

  private _goToNextNode(stepIndex: number): void {
    const el = this._document.getElementById(`node${stepIndex}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private _verifyIfHasDependencies(dependencies: string[]): void {
    if (!dependencies?.length) {
      return;
    }

    dependencies.forEach((dependencyId) => {
      this._programService.setStepDependency(dependencyId);
    });
  }

  public async getStepLogsUntilCurrent(
    programStepIndex: number
  ): Promise<ProgramLog[]> {
    let logs = await Promise.all(
      this.programStep.slice(0, programStepIndex + 1).map((step) => {
        if (
          step.type !== 'arithmetic' &&
          step.type !== 'conditional' &&
          step.type !== 'campus-integration' &&
          step.type !== 'social-integration' &&
          step.type !== 'profile-request' &&
          step.type !== 'profile-callback' &&
          step.type !== 'hidden'
        ) {
          return step.log;
        }
      })
    );

    return logs.filter((log) => log);
  }

  public async validateNode(
    groupedIndex: number,
    eventOrProgramLog: any,
    valid?: boolean
  ): Promise<void> {
    const block = this.groupedSteps[groupedIndex];

    if (!block) {
      console.warn(
        'validateNode: block not found for groupedIndex',
        groupedIndex
      );
      return;
    }

    // GROUP (MATRIX_RADIO) ----------
    if (block.kind === 'group') {
      const currentIndex = block.startIndex + eventOrProgramLog.questionIndex;
      const step = this.programStep[currentIndex];
      step.log = eventOrProgramLog.programLog;
      step.isValid = eventOrProgramLog.valid;

      const allAnswered = step.log.results.filter((r) => r).length;
      const total = block.nodes.length;

      const completed = true; //answered === total;

      if (!completed) {
        // The entire group has not yet been completed => it does not proceed with dependencies/scroll/global emit
        return;
      }

      // When completed, it updates global logs.
      const logs = await this.getStepLogsUntilCurrent(block.startIndex);
      this.logs = logs;
      this._changeDetectorRef.detectChanges();

      // Checks dependencies of the first node in the group (behavior maintained)
      if (step.dependencies) {
        this._verifyIfHasDependencies(step.dependencies);
      }

      if (
        step.type === 'radio' ||
        step.type === 'eqvas' ||
        step.type === 'date'
      ) {
        this._goToNextNode(block.startIndex);
      }

      // Emits overall validation state
      const isStepValid = !this.programStep.some((program) => !program.isValid);
      this.onStepValidChange.emit(isStepValid);

      return;
    }

    // SINGLE (old behaviour) ----------
    if (block.kind === 'single') {
      const originalIndex = block.originalIndex;

      if (valid !== undefined) {
        this.programStep[originalIndex].isValid = valid;
      } else {
        this.programStep[originalIndex].isValid =
          (eventOrProgramLog?.results?.length > 0 ||
            this.programStep[originalIndex].type === 'arithmetic') &&
          (valid ?? true);
      }

      if (eventOrProgramLog) {
        this.programStep[originalIndex].log = eventOrProgramLog;
        const logs = await this.getStepLogsUntilCurrent(originalIndex);
        this.logs = logs;
        this._changeDetectorRef.detectChanges();
      }

      const isStepValidOverall = !this.programStep.some(
        (program) => !program.isValid
      );

      const dependencies = this.programStep[originalIndex].dependencies;
      if (dependencies) {
        this._verifyIfHasDependencies(dependencies);
      }

      if (
        this.programStep[originalIndex].type === 'radio' ||
        this.programStep[originalIndex].type === 'eqvas' ||
        this.programStep[originalIndex].type === 'date'
      ) {
        this._goToNextNode(originalIndex);
      }

      this.onStepValidChange.emit(isStepValidOverall);
    }
  }
}
